import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Container, Form } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { ActionButtons, Loader, SectionHeader } from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  getAllPurchaselAccountingGroupsAsync,
  selectPurchaseAccountingGroups,
  selectPurchaseVats,
  selectPurchaseVatsLoadingStatus,
  AccountingsQueryFilters,
  PurchaseVatDetailsData,
  updatePurchaseVatAsync,
  getAllSellVatsAsync,
  getAllPurchaseVatsAsync,
} from './accountingSlice';
import { LOADING_STATUS } from '../../common/constants';

const PurchaseVatsDetails = () => {
  const backLinkUrl = '/accounting/purchasevats';
  const params = useParams();
  const dispatch = useAppDispatch();
  const purchasevats = useAppSelector(selectPurchaseVats);
  const purchaseVatsLoadingStatus = useAppSelector(
    selectPurchaseVatsLoadingStatus,
  );
  const accountingGroups = useAppSelector(selectPurchaseAccountingGroups);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filtering, setFiltering] = useState<AccountingsQueryFilters>({
    pageSize: 200,
    pageNumber: 1,
    culture: 'fi_FI',
  });

  const [editedPurchaseVatData, setEditedPurchaseVatData] =
    useState<PurchaseVatDetailsData>({
      id: '',
      name: '',
      percentage: null,
      accountingGroup: '',
    });

  const initFetch = useCallback(() => {
    dispatch(getAllPurchaselAccountingGroupsAsync(filtering));
    dispatch(getAllPurchaseVatsAsync(filtering));
  }, [dispatch]);

  useEffect(() => {
    initFetch();
  }, [initFetch]);

  // Choose purchase vats to edit by Id
  useEffect(() => {
    if (params.purchaseVatsId && purchasevats.length > 0) {
      const getPurchaseVatsId = _id =>
        purchasevats.find(({ id }) => id === params.purchaseVatsId);
      if (getPurchaseVatsId) {
        setEditedPurchaseVatData(getPurchaseVatsId);
      }
    }
  }, [params.purchaseVatsId, purchasevats]);

  // Set view loading status
  useEffect(() => {
    if (
      purchaseVatsLoadingStatus !== LOADING_STATUS.LOADING &&
      editedPurchaseVatData.id !== ''
    ) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [purchaseVatsLoadingStatus, editedPurchaseVatData.id]);

  const accountingGroupsOptions = accountingGroups.map(item => (
    <option key={item.id} value={item.id}>
      {item.name}
    </option>
  ));

  // Handle save
  const savePurchaseVats = () => {
    dispatch(updatePurchaseVatAsync(editedPurchaseVatData));
  };

  const handleChangePurchaseVat = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    if (editedPurchaseVatData != null) {
      const storedPurchaseVatsData: PurchaseVatDetailsData = {
        ...editedPurchaseVatData,
        [event.target.name]: value,
      };
      setEditedPurchaseVatData(storedPurchaseVatsData);
    }
  };

  const handleChangePurchaseVatsAccountingGroup = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const accountingGroupGuid = accountingGroupsOptions.filter(
      itemv => value === itemv.key,
    );
    if (accountingGroupGuid != null) {
      const storedPurchaseVatsData: PurchaseVatDetailsData = {
        ...editedPurchaseVatData,
        accountingGroup: accountingGroupGuid[0].key.toString(),
      };
      setEditedPurchaseVatData(storedPurchaseVatsData);
    } else {
      const storedPurchaseVatsData = {
        ...editedPurchaseVatData,
        purchaseVatId: null,
      };
      setEditedPurchaseVatData(storedPurchaseVatsData);
    }
  };

  return (
    <>
      <Helmet>
        <title>Osto alv - Määrityksen tiedot</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          BackLink={backLinkUrl}
          Breadcrumbs={[
            'Kirjanpito ja Alv-määritykset',
            'Osto Alv määritykset',
          ]}
          Title={`Alv-määrityksen tiedot: ${editedPurchaseVatData.name}`}
        />

        <Loader IsLoading={isLoading} />

        {!isLoading && (
          <Container className="container-form mt-48">
            <Form.Group controlId="name" className="mb-40">
              <Form.Label>Nimi</Form.Label>
              <Form.Control
                autoComplete="off"
                value={editedPurchaseVatData.name}
                onChange={handleChangePurchaseVat}
                name="name"
              />
            </Form.Group>

            <Form.Group controlId="percentage" className="mb-40">
              <Form.Label>Prosentti %</Form.Label>
              <Form.Control
                autoComplete="off"
                value={editedPurchaseVatData.percentage}
                onChange={handleChangePurchaseVat}
                name="percentage"
              />
            </Form.Group>

            <Form.Group controlId="accountinggroup" className="mb-40">
              <Form.Label>Kirjanpitotilit</Form.Label>
              <Form.Select
                value={editedPurchaseVatData.accountingGroup}
                onChange={handleChangePurchaseVatsAccountingGroup}
                name="accountingGroupsOptions"
              >
                {accountingGroupsOptions}
              </Form.Select>
            </Form.Group>

            <ActionButtons
              CancelButtonText="Peruuta"
              CancelButtonUrl={backLinkUrl}
              HandleSave={savePurchaseVats}
            />
          </Container>
        )}
      </Container>
    </>
  );
};

export default PurchaseVatsDetails;
